import { useEffect } from 'react'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { Preferences } from '@capacitor/preferences'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/lib/store/useAuthStore'
import i18n from '@/lib/i18n'

let isFetchingToken = false;

export function usePushNotifications() {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  useEffect(() => {
    // Only run on physical iOS/Android devices
    if (!Capacitor.isNativePlatform()) return
    
    // Only register tokens if the user is actually signed in
    if (!user) return

    let msgSub: PluginListenerHandle | null = null
    let tapSub: PluginListenerHandle | null = null

    let timeoutId: NodeJS.Timeout | null = null

    const setupPush = async () => {
      try {
        // Check local preference first. Default to enabled if not explicitly set to 'false'
        const { value: pushEnabled } = await Preferences.get({ key: 'push_enabled' })
        if (pushEnabled === 'false') {
          console.log('[PUSH TRACER] User opted out of push notifications locally. Aborting setup.')
          return
        }

        // 1. Request Permission from the OS
        const result = await FirebaseMessaging.requestPermissions()
        
        if (result.receive === 'granted') {
          // Add a safety delay for Android: Firebases's native SDK sometimes hangs 
          // if getToken() is called too quickly during app boot.
          timeoutId = setTimeout(async () => {
            if (isFetchingToken) {
              console.log('[PUSH TRACER] Already fetching token, skipping concurrent call.')
              return;
            }
            isFetchingToken = true;
            try {
              console.log('[PUSH TRACER] Firing FirebaseMessaging.getToken()...');
              const tokenResult = await FirebaseMessaging.getToken();
              const token = tokenResult.token;
              console.log('[PUSH TRACER] getToken() resolved! Token is:', token ? 'HIDDEN_FOR_SECURITY' : 'null');

              if (!token) {
                console.error('Failed to get FCM token: Token is null or undefined');
                return;
              }

              console.log('[PUSH TRACER] Firing supabase rpc...');
                // 3. Save the token, language preference, and timezone using our secure RPC
                // This bypasses RLS so the current user can claim the device token even if it previously belonged to someone else
                const { error } = await supabase.rpc('register_device_token', {
                  p_fcm_token: token,
                  p_language: i18n.language,
                  p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                })

                if (error) {
                  console.error('[PUSH TRACER] Error saving device token:', error)
                } else {
                  console.log('[PUSH TRACER] Successfully registered FCM push token!')
                }
            } catch (err) {
              console.error('[PUSH TRACER] Push notification setup failed during getToken:', err)
            } finally {
              isFetchingToken = false;
            }
          }, 2000)
        }

        // 4. Handle incoming notifications while app is OPEN (Foreground)
        msgSub = await FirebaseMessaging.addListener('notificationReceived', (event) => {
          console.log('Push message received in foreground:', event.notification)
          // We can add a Sonner toast here later if we want!
        })

        // 5. Handle tapping on a notification (Deep Linking)
        tapSub = await FirebaseMessaging.addListener('notificationActionPerformed', async (action) => {
          console.log('Push action performed:', action)
          const data = action.notification.data as Record<string, string> | undefined
          if (!data) return

          // --- AUTH RACE CONDITION FIX ---
          // Force the router to wait for Supabase to decrypt and inject the Auth Token from local storage.
          // This guarantees the subsequent React Query network request fires with full credentials, 
          // preventing the permanent 'Not Found' cache bug on cold boots.
          const { data: { session } } = await supabase.auth.getSession()
          if (!session) {
            console.log("Push Warning: Auth session still empty after wait! Deep link might fail.")
          }
          // -------------------------------

          // Route directly to the destination ("Instagram Style" Navigation)
          // We no longer build an artificial history stack. We push directly to the target.
          // This removes the need for the heavy Splashscreen and makes navigation instantly fast.
          let targetPath = '/'
          if (data.destination === 'Pick Detail Page' && data.pickId) {
            targetPath = `/pick/${data.pickId}`
          } else if (data.destination === 'Specific Collection' && data.collectionId) {
            targetPath = `/collection/${data.collectionId}`
          } else if (data.destination === 'Shared Collection' && data.collectionId) {
            targetPath = `/collection/${data.collectionId}`
          } else if (data.destination === 'Unorganized Picks') {
            targetPath = `/?tab=unorganized`
          }

          // Pass the context state in case the components need to know they came from a push
          navigate(targetPath, { state: { source: 'push_notification', collectionId: data.collectionId } })
        })

      } catch (err) {
        console.error('Push notification setup failed:', err)
      }
    }

    setupPush()

    // Listen for language changes silently to update the backend
    const handleLanguageChange = (lng: string) => {
      if (!user) return
      console.log('[PUSH TRACER] Language changed to', lng, 'upserting token...')
      setupPush() // Re-runs the registration and upserts the new language
    }
    
    i18n.on('languageChanged', handleLanguageChange)

    // Cleanup listeners when unmounting or user logs out
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      i18n.off('languageChanged', handleLanguageChange)
      if (msgSub) msgSub.remove()
      if (tapSub) tapSub.remove()
    }
  }, [user, navigate])
}
